(ns app.front.view.group.admin.fees.variable.variable-core
  (:require [app.domain.fee.model :as fee-model]
            [app.domain.fee.validate :as validate]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.column-select :as columns]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.radio :as ui-radio]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil]
            [app.front.view.group.admin.fees.formula.formula-core :as formula]
            [app.front.view.group.admin.fees.template.template-subs :as ft-subs]
            [app.front.view.group.admin.fees.variable.variable-events :as fv-events]
            [app.front.view.group.admin.fees.variable.variable-subs :as fv-subs]
            [app.front.view.group.admin.subs :as gas]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

;; Components

(defn ->id [entity-or-id]
  (if (int? entity-or-id) entity-or-id (:db/id entity-or-id)))

(defn render-fee-variable-editor
  "Renders the editor for a single fee variable, old or new"
  [{:keys [tr
           disabled?
           save-disabled?
           cancel-disabled?
           fee-templates-using-variable
           fee-variables-using-variable
           on-template-selected
           fee-schedule
           id]}]
  (let [fee-based-variables? (<sub [::ff/enabled? :feature-flag.id/fee-based-variables])
        formula-variables?   (<sub [::ff/enabled? :feature-flag.id/formula-variables])
        fee-variable         (<sub [::fv-subs/fee-variable-attrs-with-changes])
        forms                (<sub [::fv-subs/forms])
        form-fields          (<sub [::fv-subs/form-fields])
        selected-form        (<sub [::fv-subs/selected-form])
        fee-templates        (<sub [::ft-subs/fee-template-list])
        usable-variables     (<sub [::fv-subs/usable-variables])
        template-entities    (<sub [::entities/entities
                                    {:entity/parent (->id fee-schedule)
                                     :entity/type   :entity.type/fee-template}])
        valid-ids            (validate/get-referenceable-templates template-entities usable-variables)
        usable-templates     (filter (comp valid-ids :id) fee-templates)
        creating-new?        (= id :new)
        variable-used?       (boolean (or (not-empty fee-templates-using-variable)
                                          (not-empty fee-variables-using-variable)))
        {:fee-variable/keys
         [titles
          identifier]}       fee-variable
        lang                 (<sub [:language])]

    [eb/error-boundary
     [:div.col-span-2.grid-cols-2.gap-4.p-4.bg-white.items-start
      [:div.grid.grid-cols-2.gap-4
       [:h1.col-span-2.text-sm.leading-none (if creating-new?
                                              (tr [:fees/details-for-new-variable])
                                              (tr [:fees/details-for-selected-variable]))]

       [:div.col-span-2
        [ui-i18n/edit-language-selector {:allowed-locales-sub ::gas/allowed-locales}]]

       ;; Title
       [ui-i18n/localized-string-edit
        {:id                  (str "variable-title-" id)
         :type                :text
         :label-text          (tr [:fees/variable-title])
         :disabled            disabled?
         :strings             titles
         :on-change           #(rf/dispatch-sync [::fv-events/change-title id variable-used? %])
         :allowed-locales-sub ::gas/allowed-locales}]

       ;; Identifier
       [ui/text-input {:id         (str "variable-identifier-" id)
                       :type       :text
                       :label-text (tr [:fees/variable-identifier])
                       :disabled   true
                       :value      (or identifier "")}]

       (when fee-based-variables?
         [ui-radio/radio {:name            "form-fee-variable"
                          :class-name      "flex-wrap"
                          :values          (cond->> fee-model/fee-variable-source-types
                                             (not formula-variables?)
                                             (remove #{:fee-variable.source-type/formula}))
                          :value           (or (:fee-variable/source-type fee-variable) :fee-variable.source-type/form)
                          :label-text      (tr [:fees/variable-source-type])
                          :val->str        (comp tr vector)
                          :disabled-values (when disabled? fee-model/fee-variable-source-types)
                          :on-change       #(rf/dispatch-sync [::fv-events/change-field id :fee-variable/source-type %])}])

       [:div]
       (when (or (= (:fee-variable/source-type fee-variable) :fee-variable.source-type/form)
                 (nil? (:fee-variable/source-type fee-variable)))
         [:div.grid.grid-cols-2.gap-4.col-span-2
          ;; Source form
          (cc/select
            {:id         (str "variable-source-form-" id)
             :class-name "full-width-select"
             :label-text (tr [:fees/variable-source-form])
             :value      (or selected-form :none)
             :disabled   disabled?
             :on-change  #(rf/dispatch-sync [::fv-events/select-form (futil/tval %)])}
            [ui/placeholder-select-item tr :none]
            (futil/for-frag [source-form forms]
              (cc/select-item {:text  source-form
                               :value source-form})))

          ;; Source field
          (cc/select
            {:id         (str "variable-source-field-" id)
             :class-name "full-width-select"
             :label-text (tr [:fees/variable-source-field])
             :value      (or (:fee-variable/source-field fee-variable)
                             :none)
             :disabled   disabled?
             :on-change  #(rf/dispatch-sync [::fv-events/change-field id :fee-variable/source-field (-> % futil/tval keyword)])}
            [ui/placeholder-select-item tr :none]
            (futil/for-frag [source-field form-fields]
              (cc/select-item {:text  (or (:label source-field)
                                          (str (:field source-field)))
                               :value (:field source-field)})))])

       (when (= (:fee-variable/source-type fee-variable) :fee-variable.source-type/fee)
         [:div.grid.col-span-2
          [ui-select/select
           {:id           (str "variable-source-fee-" id)
            :label-text   (tr [:fees/variable-source-fee])
            :class        "full-width-select"
            :hide-label   false
            :allow-empty? true
            :select-text  (tr [:fees/choose-any-existing-fee])
            :values       usable-templates
            :on-change    #(rf/dispatch-sync [::fv-events/change-field id :fee-variable/fee-template (:id %)])
            :value        (p/find-first usable-templates #(= (:id %) (->id (:fee-variable/fee-template fee-variable))))
            :val->str     :title}]])

       (when (= (:fee-variable/source-type fee-variable) :fee-variable.source-type/formula)
         (let [variable-formula (<sub [::fv-subs/variable-formula])]
           [:div.grid.grid-cols-2.gap-4.col-span-2
            [:div.col-span-2]
            [formula/editor {:entity-id (str "variable-formula-" id)
                             :value     variable-formula
                             :disabled? disabled?
                             :variables usable-variables
                             :tr        tr
                             :on-change #(rf/dispatch-sync [::fv-events/change-formula id %])}]
            [formula/tester {:entity-id id
                             :field     :fee-variable/fee-formula
                             :formula   variable-formula
                             :variables usable-variables
                             :tr        tr}]
            [formula/variable-list tr usable-variables]
            [formula/functions-help tr]]))

       ;; Save/cancel
       [eb/error-boundary
        (when variable-used?
          [:div.col-span-2.mt-3.text-sm
           [:div.font-semibold (tr [:fees/variable-used-in])]
           [:div.mt-2.grid.grid-cols-1.md:grid-cols-2.gap-6
            (when (not-empty fee-templates-using-variable)
              [:div
               [:div.text-xs.text-gray-600.font-semibold (tr [:view/group.admin.fees])]
               [:ul.mt-2
                (futil/for-frag [fee-template fee-templates-using-variable]
                  [:li.mb-2
                   [:button.p-0.border-0.text-xs.text-blue-700
                    {:on-click #(on-template-selected fee-template)}
                    (fee-model/fee-template-title fee-template lang)]])]])
            (when (not-empty fee-variables-using-variable)
              [:div
               [:div.text-xs.text-gray-600.font-semibold (tr [:fees/variables])]
               [:ul.mt-2
                (futil/for-frag [fee-var fee-variables-using-variable]
                  [:li.mb-2
                   [:button.p-0.border-0.text-xs.text-blue-700
                    {:on-click #(>evt [::fv-events/select-fee-variable (:db/id fee-var)])}
                    (fee-model/fee-variable-title fee-var lang)]])]])]])

        (when-not disabled?
          [:div.col-span-2.flex.justify-end
           [ui/modal-confirmation-button
            {:tr                  tr
             :id                  "delete-fee-variable"
             :on-yes-click        #(>evt [::fv-events/delete id])
             :disabled?           (or creating-new? variable-used?)
             :trigger-button-text (tr [:delete])
             :trigger-button-opts {:kind       :tertiary
                                   :class-name "mr-4"}}
            (tr [:fees/confirm-delete-fee-variable])]
           [ui/modal-confirmation-button
            {:tr                  tr
             :id                  "cancel-fee-template-editing"
             :on-yes-click        #(>evt [::fv-events/cancel id])
             :trigger-button-text (tr [:cancel])
             :trigger-button-opts {:kind     :tertiary
                                   :disabled cancel-disabled?}}
            (tr [:fees/confirm-cancel-fee-template-editing])]
           (cc/button {:disabled save-disabled?
                       :on-click #(>evt [::fv-events/save id])}
                      (tr [(if creating-new? :create :save)]))])]]]]))


(defn view [{:keys [tr on-template-selected fee-schedule]}]
  (let [creating-new?                (<sub [::fv-subs/creating-new?])
        disabled?                    (<sub [::ft-subs/editor-disabled?])
        fee-variables                (<sub [::fv-subs/fee-variable-list])
        fee-variable                 (<sub [::fv-subs/selected-fee-variable])
        current-id                   (<sub [::fv-subs/selected-fee-variable-id])
        save-disabled?               (<sub [::fv-subs/save-disabled?])
        cancel-disabled?             (and (not creating-new?) save-disabled?)
        fee-templates-using-variable (<sub [::fv-subs/fee-templates-using-variable fee-variable])
        fee-variables-using-variable (<sub [::fv-subs/fee-variables-using-variable fee-variable])]
    [eb/error-boundary
     [:div.grid.grid-cols-3.gap-4.p-4.bg-ui-light-gray
      [:h1.col-span-3.text-sm.font-bold.leading-none (tr [:fees/fee-variables])]
      [:div.col-span-1.py-4.px-2.bg-white
       [columns/column-select (p/assoc-if {:tr           tr
                                           :title        :fees/variables
                                           :options      fee-variables
                                           :on-change    #(>evt [::fv-events/select-fee-variable %])
                                           :height       "600px"
                                           :transparent? true
                                           :disabled?    creating-new?}
                                          :actions (when-not disabled?
                                                     [{:title    (tr [:fees/new-fee-variable])
                                                       :on-click #(>evt [::fv-events/create-new-fee-variable])}]))]]
      (if (or fee-variable creating-new?)
        [render-fee-variable-editor (util/mmap {:id current-id}
                                               tr disabled? save-disabled? cancel-disabled?
                                               fee-templates-using-variable
                                               fee-variables-using-variable
                                               on-template-selected fee-schedule)]
        [:div.col-span-2.p-4.bg-white.flex.justify-center.items-center
         [:span.font-bold.uppercase.tracking-wider.text-gray-500 (tr [:fees/select-or-create-a-variable])]])]]))
